import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatStorageBytes } from "@/domains/documents/services/buildingStorageQuota";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatBillingMonthlyPrice } from "../format";
import type { BillingPlan, BuildingSubscription } from "../types";
import { useBillingPlanTooltip } from "../useBillingPlanTooltip";

export function BillingPlanOverview({
  plan,
  usage,
}: {
  plan: BillingPlan;
  usage: BuildingSubscription["usage"];
}) {
  const { t, language } = useLanguage();
  const copy = t.billing.plans;
  const { open, onOpenChange, triggerProps, onPointerDownOutside } = useBillingPlanTooltip();
  const allowance = (value: number | null, used: number) =>
    value === null
      ? copy.unlimited
      : fill(copy.usage, { used: String(used), count: String(value) });
  const limits = [
    [
      copy.storage,
      plan.storage_limit_bytes === null
        ? copy.unlimited
        : formatStorageBytes(plan.storage_limit_bytes),
    ],
    [copy.projects, allowance(plan.max_projects, usage.projects)],
    [copy.chatQuestions, allowance(plan.chat_questions_per_month, usage.chat_questions)],
    [copy.pmIssues, allowance(plan.max_pm_issues, usage.pm_issues)],
  ];
  return (
    <div className="flex items-center gap-1 whitespace-nowrap text-xs lg:gap-2 lg:text-sm">
      <span className="rounded-full bg-neutral-100 px-2 py-1 font-medium text-neutral-700">
        {copy.names[plan.code]}
      </span>
      <span className="text-muted-foreground">
        {fill(copy.monthlyPrice, { amount: formatBillingMonthlyPrice(plan.rate_cents, language) })}
      </span>
      <Tooltip open={open} onOpenChange={onOpenChange} delayDuration={150}>
        <TooltipTrigger asChild>
          <button
            {...triggerProps}
            type="button"
            aria-label={copy.limitsInfo}
            className="inline-flex shrink-0 rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm" onPointerDownOutside={onPointerDownOutside}>
          <dl className="space-y-1">
            {limits.map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="shrink-0 font-medium text-popover-foreground">{value}</dd>
              </div>
            ))}
          </dl>
          {plan.code === "free" && <p className="mt-2 max-w-xs text-xs">{copy.freeLimit}</p>}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
